import dayjs from "dayjs";
import { ReactElement, useMemo } from "react";
import { Button, Icon } from "@sellar-io/components";
import { useMediaQuery } from "usehooks-ts";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
} from "../../../components/modal/Dialog";
import { InputContainer } from "@sellar-io/components";
import { InputText } from "@sellar-io/components";
import { Toast } from "../../../components/toast/Toast";
import { useToast } from "../../../components/toast/useToast.shared";
import { useOrderEventList } from "../../../hooks/api/v2/orderEvent/useOrderEventList";
import { PAYMENT_LINK_REF } from "../../../constants/routes";

export const ViewPaymentLinkModal = ({
  active,
  onDismiss,
  orderId,
  paymentLinkCode,
  retailerInvoiceEmails,
}: {
  active: boolean;
  onDismiss: () => void;
  orderId: number;
  paymentLinkCode: string;
  retailerInvoiceEmails: string[];
}): ReactElement => {
  const { toast } = useToast();
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const link = `${import.meta.env.VITE_URL}${PAYMENT_LINK_REF.href({ code: paymentLinkCode })}`;

  const orderEventListQuery = useOrderEventList(
    {
      query: [{ type: "OrderId", modifier: "EQUALS", value: orderId }],
      sortBy: "createdAt",
      sortDir: "desc",
    },
    { enabled: active },
  );

  const lastSentAt = useMemo(() => {
    const events =
      orderEventListQuery.data?.pages.flatMap((page) => page) ?? [];
    const event = events.find((e) => e.data.type === "payment-link-sent");
    return event?.createdAt ?? null;
  }, [orderEventListQuery.data]);

  const copyLink = () => {
    toast({
      placement: "bottom",
      render: () => <Toast icon={"LINK"}>Link copied!</Toast>,
    });
    navigator.clipboard.writeText(link);
  };

  return (
    <Dialog active={active} onDismiss={onDismiss}>
      <DialogNavigation
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={onDismiss}
        title={"Payment link"}
      >
        {lastSentAt && retailerInvoiceEmails.length > 0 && (
          <p className="-mt-5 text-center text-sm text-zinc-700">
            This link was last sent to{" "}
            <span className="font-semibold">
              {retailerInvoiceEmails.join(", ")}
            </span>{" "}
            on {dayjs(lastSentAt).format("DD/MM/YYYY")}.
          </p>
        )}
      </DialogNavigation>
      <DialogContent className={"flex flex-col gap-4 px-4 pb-4"}>
        <div className="flex flex-row gap-2">
          <InputContainer className="w-full truncate pr-0 pl-3">
            <InputText
              placeholder={""}
              readOnly={true}
              value={link}
              disabled={true}
              className={"h-full self-stretch truncate"}
            />
            <Button
              type={"button"}
              color={"secondary"}
              className={
                "border-t-none -m-3 self-stretch rounded-l-none border-y-0 border-r-0 border-gray-300 p-3"
              }
              onClick={copyLink}
            >
              <Icon size={1} icon={"CONTENT_COPY"} />
              {isDesktop && "Copy"}
            </Button>
          </InputContainer>
        </div>
      </DialogContent>
    </Dialog>
  );
};
